import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import FileUploader from './FileUploader';
import NutrientValidationSummaryModal from './NutrientValidationSummaryModal';
import { nutrientBulkService } from '../utils/api'; // Import the new nutrient bulk service

// Error message component (adapted for nutrients)
const ValidationErrorMessage = ({ error }) => {
  const { t } = useTranslation();
  let parsedError = error;
  try {
    if (typeof error === 'string' && error.startsWith('{') && error.endsWith('}')) {
      const errorObj = JSON.parse(error);
      if (errorObj.error) {
        parsedError = errorObj.error;
      }
    }
  } catch (e) { }

  const getDetailedErrorMessage = () => {
    const errorText = parsedError;
    if (errorText.includes('empty') || errorText.includes('invalid')) {
      return t('nutrientutils.fileEmpty');
    }
    if (errorText.includes('Required columns') || errorText.includes('Dish ID') || errorText.includes('Reference Name')) {
      return errorText; // Show specific server message for column issues
    }
    if (errorText.includes('Missing required nutrient columns')) {
      return t('nutrientutils.missingNutrientColumns', { message: errorText });
    }
    if (errorText.includes('Invalid value') || errorText.includes('non-negative number')) {
      return t('nutrientutils.invalidNumericValue');
    }
     if (errorText.includes('does not exist or does not belong')) {
      return t('nutrientutils.invalidDishId', { message: errorText });
    }
    return errorText;
  };

  return (
    <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-3">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            {t('nutrientutils.validationError')}
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{t('nutrientutils.validationErrorDetails')}</p>
            <p className="font-medium mt-1">{getDetailedErrorMessage()}</p>
            <div className="mt-3 p-3 bg-gray-50 rounded-md">
              <h4 className="font-medium mb-1 text-gray-700">{t('common.help')}:</h4>
              <ul className="list-disc pl-5 space-y-1 text-gray-600">
                <li>{t('nutrientutils.wrongFileFormat')}</li>
                <li>{t('nutrientutils.fileStructureExample')}</li>
                <li>{t('nutrientutils.tryExportFirst')}</li>
                <li>{t('nutrientutils.checkUnits')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component for bulk nutrient operations
 * 
 * @param {Object} props
 * @param {number} props.schoolId - The ID of the current school
 * @param {Array<{id: number, name: string, unit: string | null}>} props.enabledNutrients - Array of enabled nutrients for the school
 * @param {Function} props.onImportComplete - Callback when import is complete
 */
const BulkNutrientSection = ({ schoolId, enabledNutrients, onImportComplete }) => {
  const { t, i18n } = useTranslation();
  const [isExportingMissing, setIsExportingMissing] = useState(false);
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Combined exporting state for disabling buttons
  const isExporting = isExportingMissing || isExportingAll;

  // Reset all states
  const resetStates = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setValidationError(null);
    setIsImporting(false);
    setImportResult(null);
  };
  
  // Handle export missing nutrients button click
  const handleExportMissing = async () => {
    try {
      setIsExportingMissing(true);
      const uiLanguage = i18n.language;
      const refNameHeaderText = t('excelHeaders.referenceName'); // Get localized header
      const result = await nutrientBulkService.exportNutrients(
        schoolId, 
        'missing', 
        uiLanguage,
        refNameHeaderText // Pass localized header
      );
      saveAs(result.blob, result.filename);
    } catch (error) {
      console.error('Error exporting missing nutrients:', error);
      alert(t('nutrientutils.exportError', { error: error.message }));
    } finally {
      setIsExportingMissing(false);
    }
  };
  
  // Handle export all nutrients button click
  const handleExportAll = async () => {
    try {
      setIsExportingAll(true);
      const uiLanguage = i18n.language;
      const refNameHeaderText = t('excelHeaders.referenceName'); // Get localized header
      const result = await nutrientBulkService.exportNutrients(
        schoolId, 
        'all', 
        uiLanguage,
        refNameHeaderText // Pass localized header
      );
      saveAs(result.blob, result.filename);
    } catch (error) {
      console.error('Error exporting all nutrients:', error);
      alert(t('nutrientutils.exportError', { error: error.message }));
    } finally {
      setIsExportingAll(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setValidationResult(null);
    setValidationError(null);
    setImportResult(null);
  };
  
  // Handle file validation
  const handleValidate = async () => {
    if (!selectedFile) return;
    
    try {
      setIsValidating(true);
      setValidationError(null);
      setValidationResult(null); // Reset previous result
      setImportResult(null); // Reset import result
      
      // Get UI language and localized reference name header
      const uiLanguage = i18n.language;
      const refNameHeaderText = t('excelHeaders.referenceName');
      
      // Pass language and header to the validation service
      const result = await nutrientBulkService.validateNutrientFile(
        selectedFile, 
        schoolId, 
        uiLanguage,
        refNameHeaderText
      );
      
      setValidationResult(result);
      setIsModalOpen(true); 
    } catch (error) {
      console.error('Error validating nutrient file:', error);
      setValidationError(error.message);
    } finally {
      setIsValidating(false);
    }
  };
  
  // Handle import confirmation
  const handleImportConfirm = async () => {
    if (!validationResult || !validationResult.validationId) return;
    
    try {
      setIsImporting(true);
      
      const result = await nutrientBulkService.importNutrients(validationResult.validationId, schoolId);
      
      setImportResult(result); // Set the result to show success/failure in modal
      setIsImporting(false);
      
      // Call the onImportComplete callback ONLY if the import was successful
      if (result.success && onImportComplete) {
        onImportComplete(result); // Pass the full result including updatedMissingItems
      }
      
    } catch (error) {
      console.error('Error importing nutrients:', error);
      // Set import result to reflect error in modal
      setImportResult({ success: false, message: error.message || t('nutrientutils.importErrorGeneric') });
      setIsImporting(false);
      // No need for alert here, modal will show error
    }
  };
  
  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    
    // If import finished (success or fail), reset file/validation state
    if (importResult) {
      setTimeout(() => {
        resetStates(); 
      }, 300); // Delay to allow modal close animation
    }
  }, [importResult]);

  return (
    <div className="bg-white rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        {t('nutrientutils.bulkNutrientsTitle')}
      </h2>
      
      <div className="space-y-6">
        {/* Step 1: Export */} 
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('nutrientutils.step1')} 
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('nutrientutils.exportDescription')}
          </p>
          <div className="flex space-x-3">
            {/* Export Missing Button */}
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isExporting ? 'opacity-75 cursor-wait' : ''}`}
              onClick={handleExportMissing}
              disabled={isExporting}
            >
              {isExportingMissing ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              ) : (
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              )}
              {isExportingMissing ? t('common.exporting') : t('nutrientutils.exportMissingButton')}
            </button>
            {/* Export All Button */}
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isExporting ? 'opacity-75 cursor-wait' : ''}`}
              onClick={handleExportAll}
              disabled={isExporting}
            >
              {isExportingAll ? (
                 <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              ) : (
                 <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              )}
               {isExportingAll ? t('common.exporting') : t('nutrientutils.exportAllButton')}
            </button>
          </div>
        </div>
        
        {/* Step 2: Upload */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('nutrientutils.step2')} 
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('nutrientutils.importDescription')} 
          </p>
          <FileUploader
            onFileSelect={handleFileSelect}
            acceptedFileTypes=".xlsx"
            maxSizeMB={5}
            label={t('nutrientutils.selectFileButton')} 
            disabled={isValidating || isImporting}
          />
          {validationError && (
            <ValidationErrorMessage error={validationError} />
          )}
        </div>
        
        {/* Step 3: Validate & Import */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('nutrientutils.step3')} 
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('nutrientutils.validateDescription')} 
          </p>
          <button
            type="button"
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${!selectedFile || isValidating ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={handleValidate}
            disabled={!selectedFile || isValidating}
          >
            {isValidating ? (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : (
              <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            )}
            {isValidating ? t('common.validating') : t('nutrientutils.validateImportButton')}
          </button>
          {selectedFile && (
             <p className="text-sm text-gray-600 truncate mt-2">
              {t('common.selected')}: <span className="font-medium">{selectedFile.name}</span>
            </p>
          )}
        </div>
      </div>
      
      {/* Validation/Import summary modal - USE THE NEW NUTRIENT MODAL */}
      <NutrientValidationSummaryModal 
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleImportConfirm}
        validationResult={validationResult}
        isImporting={isImporting}
        importResult={importResult}
        // Pass nutrient-specific translations/text
        itemType={t('nutrientutils.itemType', { defaultValue: 'Dishes' })} 
        confirmButtonText={t('nutrientutils.confirmImportButton', { defaultValue: 'Import Nutrients'})}
        successMessage={t('nutrientutils.importSuccessMessage', { defaultValue: 'Nutrient import successful'})}
        modalTitle={t('nutrientutils.validationModalTitle', { defaultValue: 'Nutrient Import Validation'})}
      />
    </div>
  );
};

export default BulkNutrientSection; 